import {
  Modal,
  Stack,
  NumberInput,
  Textarea,
  Button,
  Group,
  Alert,
  Text,
  Loader,
  Combobox,
  InputBase,
  useCombobox,
  ActionIcon,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconAlertCircle, IconPencil, IconX } from '@tabler/icons-react';
import { useState, useMemo, useEffect } from 'react';
import { useDosingChemicals, useUpdateManualDosing } from '@/hooks/useDosingEvents';
import type { DosingEvent, ManualDosingUpdateRequest } from '@/types/dosing';

interface EditDosingModalProps {
  opened: boolean;
  onClose: () => void;
  event: DosingEvent | null;
}

// Common brands for autocomplete (alphabetized, same as ManualDosingModal)
const COMMON_BRANDS = [
  'Aquaforest',
  'ATI',
  'Brightwell Aquatics',
  'BRS',
  'DIY',
  'ESV',
  'Fauna Marin',
  'Red Sea',
  'Reef Zlements',
  'Seachem',
  'Tropic Marin',
  'Two Little Fishies',
];

// Brand selection combobox (Mantine v8 - Select creatable was removed)
interface BrandComboboxProps {
  value: string;
  onChange: (value: string) => void;
}

function BrandCombobox({ value, onChange }: BrandComboboxProps) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const filteredOptions = useMemo(() => {
    const search = value.toLowerCase().trim();
    if (!search) return COMMON_BRANDS;
    return COMMON_BRANDS.filter((brand) =>
      brand.toLowerCase().includes(search)
    );
  }, [value]);

  const showCreateOption = value.trim() &&
    !COMMON_BRANDS.some(b => b.toLowerCase() === value.toLowerCase().trim());

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        onChange(val);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          label="Brand (optional)"
          placeholder="Select or type brand"
          value={value}
          onChange={(e) => {
            onChange(e.currentTarget.value);
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          rightSection={
            value ? (
              <ActionIcon
                size="sm"
                variant="subtle"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange('');
                }}
              >
                <IconX size={14} />
              </ActionIcon>
            ) : null
          }
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {filteredOptions.map((brand) => (
            <Combobox.Option key={brand} value={brand}>
              {brand}
            </Combobox.Option>
          ))}
          {showCreateOption && (
            <Combobox.Option value={value.trim()}>
              + Add "{value.trim()}"
            </Combobox.Option>
          )}
          {filteredOptions.length === 0 && !showCreateOption && (
            <Combobox.Empty>No brands found</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

export function EditDosingModal({ opened, onClose, event }: EditDosingModalProps) {
  // Form state
  const [chemicalName, setChemicalName] = useState('');
  const [volumeMl, setVolumeMl] = useState<number | string>(0);
  const [recordedAt, setRecordedAt] = useState<Date | null>(new Date());
  const [brand, setBrand] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Combobox for chemical selection
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // Fetch chemicals for autocomplete
  const { data: chemicalsData, isLoading: chemicalsLoading } = useDosingChemicals();
  const updateMutation = useUpdateManualDosing();

  // Pre-fill form when event changes
  useEffect(() => {
    if (event && opened) {
      // DosingEvent uses chemical_name or pump_name
      setChemicalName(event.chemical_name || event.pump_name || '');
      setVolumeMl(event.volume_ml ?? 0);
      // DosingEvent uses timestamp, not recorded_at
      setRecordedAt(event.timestamp ? new Date(event.timestamp) : new Date());
      setBrand(event.brand || '');
      // DosingEvent uses user_note, not notes
      setNotes(event.user_note || '');
      setError(null);
    }
  }, [event, opened]);

  // Prepare chemical options
  const chemicalOptions = useMemo(() => {
    if (!chemicalsData?.chemicals) return [];
    return chemicalsData.chemicals.map((c) => ({
      value: c.name,
      label: c.name,
      category: c.category,
    }));
  }, [chemicalsData]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    const search = chemicalName.toLowerCase().trim();
    if (!search) return chemicalOptions;
    return chemicalOptions.filter((opt) =>
      opt.label.toLowerCase().includes(search)
    );
  }, [chemicalOptions, chemicalName]);

  // Reset form
  const resetForm = () => {
    setChemicalName('');
    setVolumeMl(0);
    setRecordedAt(new Date());
    setBrand('');
    setNotes('');
    setError(null);
  };

  // Handle close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Handle submit
  const handleSubmit = async () => {
    setError(null);

    if (!event?.id) {
      setError('No event to update');
      return;
    }

    // Validation
    if (!chemicalName.trim()) {
      setError('Chemical name is required');
      return;
    }

    const volume = typeof volumeMl === 'string' ? parseFloat(volumeMl) : volumeMl;
    if (!volume || volume <= 0) {
      setError('Volume must be greater than 0');
      return;
    }

    if (!recordedAt) {
      setError('Date and time is required');
      return;
    }

    // Check not in future (with 5 min buffer)
    const now = new Date();
    const maxAllowed = new Date(now.getTime() + 5 * 60 * 1000);
    if (recordedAt > maxAllowed) {
      setError('Date cannot be in the future');
      return;
    }

    const request: ManualDosingUpdateRequest = {
      chemical_name: chemicalName.trim(),
      volume_ml: volume,
      recorded_at: recordedAt.toISOString(),
      brand: brand.trim() || undefined,
      notes: notes.trim() || undefined,
    };

    try {
      await updateMutation.mutateAsync({
        eventId: event.id,
        data: request,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update entry');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconPencil size={20} />
          <Text fw={600}>Edit Dosing Entry</Text>
        </Group>
      }
      size="md"
      centered
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            onClose={() => setError(null)}
            withCloseButton
          >
            {error}
          </Alert>
        )}

        {/* Chemical Name - Combobox with autocomplete */}
        <Combobox
          store={combobox}
          onOptionSubmit={(val) => {
            setChemicalName(val);
            combobox.closeDropdown();
          }}
        >
          <Combobox.Target>
            <InputBase
              label="Chemical"
              placeholder="Select or type chemical name"
              required
              value={chemicalName}
              onChange={(e) => {
                setChemicalName(e.currentTarget.value);
                combobox.openDropdown();
                combobox.updateSelectedOptionIndex();
              }}
              onClick={() => combobox.openDropdown()}
              onFocus={() => combobox.openDropdown()}
              onBlur={() => combobox.closeDropdown()}
              rightSection={chemicalsLoading ? <Loader size={16} /> : null}
            />
          </Combobox.Target>

          <Combobox.Dropdown>
            <Combobox.Options>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => (
                  <Combobox.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Combobox.Option>
                ))
              ) : (
                <Combobox.Empty>
                  {chemicalName.trim()
                    ? `Press Enter to use "${chemicalName}"`
                    : 'No chemicals found'}
                </Combobox.Empty>
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>

        {/* Volume */}
        <NumberInput
          label="Volume (mL)"
          placeholder="60"
          required
          min={0.1}
          max={10000}
          decimalScale={1}
          step={5}
          value={volumeMl}
          onChange={setVolumeMl}
        />

        {/* Date/Time */}
        <DateTimePicker
          label="Date & Time"
          required
          value={recordedAt}
          onChange={(val) => {
            if (val) {
              setRecordedAt(new Date(val));
            }
          }}
          maxDate={new Date()}
          clearable={false}
        />

        {/* Brand - Combobox with autocomplete (Mantine v8 pattern) */}
        <BrandCombobox
          value={brand}
          onChange={setBrand}
        />

        {/* Notes */}
        <Textarea
          label="Notes (optional)"
          placeholder="Any additional notes..."
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          maxLength={500}
          autosize
          minRows={2}
          maxRows={4}
        />

        {/* Actions */}
        <Group justify="flex-end" gap="xs" mt="md">
          <Button
            variant="subtle"
            onClick={handleClose}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={updateMutation.isPending}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
